import { useMemo, useRef, useState } from 'react'
import { Loader2, Trash2 } from 'lucide-react'
import type {
  AddDictionaryWordOutcome,
  ClearDictionaryOutcome,
  RemoveDictionaryWordOutcome,
} from '@/api/types/dictionary'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { sortDictionaryWords } from './helpers/sort-dictionary-words'
import { ClearDictionaryDialog } from './components/clear-dictionary-dialog'
import { pasteTrimmedText } from './handlers/paste-trimmed-text'
import { useAddWordField } from './hooks/use-add-word-field'
import { useClearAll } from './hooks/use-clear-all'
import { useDictionaryLoadErrorToast } from './hooks/use-dictionary-load-error-toast'
import { useKeepListPosition } from './hooks/use-keep-list-position'
import { useRemoveWord } from './hooks/use-remove-word'
import type { DictionaryStatus } from './types/dictionary-status'

export interface EditDictionaryTabProps {
  /** Until `ready` the words aren't known, so neither a count nor the empty state is shown. */
  status: DictionaryStatus
  customWords: string[]
  /** The Check Spelling tab's Case Sensitive setting, which decides what counts as a duplicate. */
  caseSensitive: boolean
  /** Saves the word and resolves once the backend has answered; the list updates from the cache. */
  onAddWord: (word: string) => Promise<AddDictionaryWordOutcome>
  /** Removes the word and resolves once the backend has answered; the list updates from the cache. */
  onRemoveWord: (word: string) => Promise<RemoveDictionaryWordOutcome>
  /** Clears every word and resolves once the backend has answered; the list updates from the cache. */
  onClearAll: () => Promise<ClearDictionaryOutcome>
}

const ADD_WORD_MESSAGE_ID = 'add-word-message'

// Brings a just-added word into view when its row mounts in its A–Z place.
const scrollIntoViewOnMount = (row: HTMLLIElement | null) => {
  row?.scrollIntoView?.({ block: 'nearest' })
}

/** Figma "Check Spelling Panel / State=Dictionary" (and "Dictionary Empty"). */
export function EditDictionaryTab({
  status,
  customWords,
  caseSensitive,
  onAddWord,
  onRemoveWord,
  onClearAll,
}: EditDictionaryTabProps) {
  const [announcement, setAnnouncement] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const clearAllButtonRef = useRef<HTMLButtonElement>(null)
  const isReady = status === 'ready'
  const sortedWords = useMemo(() => sortDictionaryWords(customWords), [customWords])
  const addField = useAddWordField({
    isReady,
    customWords,
    caseSensitive,
    onAddWord,
    onAnnounce: setAnnouncement,
  })
  const removal = useRemoveWord({
    sortedWords,
    onRemoveWord,
    onAnnounce: setAnnouncement,
    listRef,
    inputRef,
  })
  const clearAll = useClearAll({
    onClearAll,
    onAnnounce: setAnnouncement,
    inputRef,
    clearAllButtonRef,
  })

  const rememberListPosition = useKeepListPosition({
    listRef,
    sortedWords,
    addedWord: addField.addedWord,
  })

  useDictionaryLoadErrorToast(status)

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            className="min-w-16 flex-1"
            placeholder="Add a word…"
            aria-label="Add a word"
            aria-describedby={addField.message ? ADD_WORD_MESSAGE_ID : undefined}
            error={Boolean(addField.message)}
            disabled={!isReady}
            // Read-only, not disabled, while saving, so keyboard focus stays in the field.
            readOnly={addField.isSaving}
            value={addField.draft}
            onChange={(e) => addField.setDraft(e.target.value)}
            onPaste={(e) => pasteTrimmedText(e, addField.setDraft)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void addField.submit()
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            className="text-xs"
            disabled={!addField.canAdd}
            onClick={() => void addField.submit()}
          >
            Add
          </Button>
        </div>
        {addField.message && (
          <p id={ADD_WORD_MESSAGE_ID} role="alert" className="text-xs text-destructive">
            {addField.message}
          </p>
        )}
      </div>

      <Separator />

      {status === 'loading' && (
        <p className="flex items-center justify-center gap-2 py-6 text-[13px] text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading dictionary…
        </p>
      )}

      {status === 'error' && (
        // Figma "Failure Notifications / Dictionary Load Failure": the header without a count or
        // Clear All, since the words aren't known, and why the list is missing.
        <>
          <div className="flex h-8 items-center">
            <p className="text-xs leading-none font-medium text-foreground">Custom Words</p>
          </div>
          <p className="py-6 text-center text-[13px] text-muted-foreground">
            Custom words couldn&apos;t be loaded.
          </p>
        </>
      )}

      {isReady && (
        <>
          <div className="flex h-8 items-center justify-between">
            <p className="text-xs leading-none font-medium text-foreground">
              Custom Words ({sortedWords.length})
            </p>
            {sortedWords.length > 0 && (
              <Button
                ref={clearAllButtonRef}
                variant="ghost"
                size="sm"
                className="text-xs text-page-editor-flow-text hover:text-page-editor-flow-text"
                onClick={() => clearAll.setDialogOpen(true)}
              >
                Clear All
              </Button>
            )}
          </div>

          {sortedWords.length === 0 ? (
            <p className="py-6 text-center text-[13px] text-muted-foreground">
              No custom words yet.
            </p>
          ) : (
            // The list fills the panel's remaining height and scrolls inside it (UX); on a short
            // panel it keeps three rows and the panel scrolls instead. A scroll box clips what is
            // drawn outside it, so 4px of padding (cancelled by a -4px margin, so the rows don't
            // move) leaves room for the 3px focus ring.
            <ul
              ref={listRef}
              className="-m-1 flex min-h-[104px] flex-1 flex-col overflow-y-auto p-1"
              onScroll={rememberListPosition}
            >
              {sortedWords.map((word) => (
                <li
                  key={word}
                  data-word={word}
                  ref={word === addField.addedWord ? scrollIntoViewOnMount : undefined}
                  className="flex h-8 items-center justify-between rounded-md px-0.5 hover:bg-accent"
                >
                  <span className="truncate text-[13px] text-foreground">{word}</span>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    // No press nudge here: inside the scrolling list it pushes past the bottom edge,
                    // the scrollbar appears mid-click and moves the button, and the click is lost.
                    className="active:translate-y-0"
                    aria-label={`Remove "${word}" from dictionary`}
                    // aria-disabled, not disabled, while removing: disabling would drop keyboard focus.
                    aria-disabled={removal.removingWords.has(word) || undefined}
                    onClick={() => void removal.remove(word)}
                    onFocus={() => removal.trackFocus(word)}
                    onBlur={removal.untrackFocus}
                  >
                    <Trash2 />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <ClearDictionaryDialog
        open={clearAll.isDialogOpen}
        onOpenChange={clearAll.setDialogOpen}
        wordCount={sortedWords.length}
        isClearing={clearAll.isClearing}
        onConfirm={() => void clearAll.confirm()}
        onCloseAutoFocus={clearAll.handleCloseAutoFocus}
      />

      <p className="sr-only" role="status" aria-live="polite">
        {announcement}
      </p>
    </div>
  )
}
